import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, App, Button, Card, Col, Popconfirm, Progress, Row, Segmented, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { AimOutlined, CaretRightOutlined, CheckCircleOutlined, MailOutlined, SyncOutlined, EyeOutlined, MinusCircleOutlined, SendOutlined, StopOutlined, TeamOutlined, UserDeleteOutlined, WarningOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import { CardTitle, StatCard } from '../../components/StatCard';
import { NameCell, TableSearch } from '../../components/NameCell';
import type { CampaignItem, RecipientItem, ResendSyncState } from '@crm/shared';
import { campaignReportsMissing } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { errorMessage } from '../../api/client';
import { pct, resendSyncActive, useCancelCampaign, useRecipients, useResumeCampaign, useSyncCampaignStatus, useUpdateCampaign } from '../../features/campaigns/api';
import { contactName } from '../../features/contacts/api';
import { STATUS_COLOR, heldText, statusLabel } from './CampaignsPage';
import { SentEmailModal } from './SentEmailModal';
import { ProjectSelect } from '../../components/ProjectSelect';
import { useProjectScope } from '../../features/projects/scope';
import { EmptyState, tableLoading, tableLocale } from '../../components/states';
import { fmtDateTime, plural } from '../../utils/format';

const REC_COLOR: Record<string, string> = { PENDING: 'default', SENT: 'blue', DELIVERED: 'cyan', OPENED: 'success', CLICKED: 'success', BOUNCED: 'error', COMPLAINED: 'error', FAILED: 'error', SKIPPED: 'default' };

export function CampaignStatsPage({ campaign: c }: { campaign: CampaignItem }) {
  const { message } = App.useApp();
  const rename = useUpdateCampaign();
  const cancel = useCancelCampaign();
  const resume = useResumeCampaign();
  const sync = useSyncCampaignStatus();
  const syncing = resendSyncActive(c.resendSync);
  const scope = useProjectScope();
  // undefined = closed, null = the campaign's first sent copy, else one recipient's copy
  const [viewing, setViewing] = useState<{ id: string; email: string } | null | undefined>(undefined);
  // people not emailed yet (or whose batch Resend refused): what a resume would send to
  const left = useRecipients(c.id, { status: 'PENDING', page: 1, pageSize: 1 }, `${c.status}:${c.sentCount}`);
  const stopped = c.status === 'FAILED' || c.status === 'CANCELLED';
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState<string | undefined>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // re-fetch whenever the campaign's counters change (polling while sending)
  const recipients = useRecipients(c.id, { status, page, pageSize, q }, `${c.status}:${c.sentCount}:${c.deliveredCount}:${c.openedCount}:${c.clickedCount}:${c.bouncedCount}`);
  const stat = (icon: ReactNode, title: string, value: number, of?: number, ofLabel = 'of sent') => (
    <Col xs={24} sm={12} xl={6}>
      <StatCard icon={icon} label={title} value={value.toLocaleString()} note={of !== undefined ? (of ? `${pct(value, of)} ${ofLabel}` : 'Nothing sent yet') : 'Matched the audience'} />
    </Col>
  );
  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Campaigns', to: '/campaigns' }, { title: c.name }]}
        docTitle={c.name}
        title={
          <Typography.Text
            style={{ font: 'inherit', color: 'inherit' }}
            editable={{
              tooltip: 'Rename campaign',
              maxLength: 160,
              onChange: (name) => {
                const next = name.trim();
                if (next && next !== c.name) rename.mutateAsync({ id: c.id, name: next }).then(() => message.success('Campaign renamed')).catch((e) => message.error(errorMessage(e)));
              },
            }}
          >
            {c.name}
          </Typography.Text>
        }
        subtitle={
          <Space wrap>
            {c.status === 'SENDING' && c.heldAt ? <Tag color="warning" style={{ marginInlineEnd: 0 }}>Waiting</Tag> : <Tag color={STATUS_COLOR[c.status]} style={{ marginInlineEnd: 0 }}>{statusLabel(c.status)}</Tag>}
            {scope.enabled && (
              <ProjectSelect
                style={{ minWidth: 180 }}
                value={c.projectId}
                onChange={(projectId) =>
                  rename
                    .mutateAsync({ id: c.id, projectId })
                    .then(() => message.success(projectId ? `Added to ${scope.byId(projectId)?.name ?? 'the project'}` : 'Removed from its project'))
                    .catch((e) => message.error(errorMessage(e)))
                }
              />
            )}
            <Typography.Text type="secondary">From {c.agent?.name ?? 'a deleted agent'} · "{c.subject}"</Typography.Text>
            {c.status === 'VERIFYING' && <Typography.Text type="secondary">· checking recipient emails before sending</Typography.Text>}
            {c.completedAt && <Typography.Text type="secondary">· finished {fmtDateTime(c.completedAt)}</Typography.Text>}
            {c.error && <Typography.Text type="danger">{c.error}</Typography.Text>}
          </Space>
        }
        extra={
          <Space wrap>
            {c.sentCount > 0 && (
              <Button icon={<MailOutlined />} onClick={() => setViewing(null)}>
                View email
              </Button>
            )}
            {c.sentCount > 0 && (
              <Tooltip title="Fetches each email's latest status from Resend (delivered, opened, clicked, bounced) and records anything missed. Bounced addresses are marked invalid so future campaigns skip them.">
                <Button
                  icon={<SyncOutlined spin={syncing} />}
                  loading={sync.isPending}
                  disabled={syncing}
                  onClick={() =>
                    sync
                      .mutateAsync(c.id)
                      .then(() => message.success('Sync with Resend started. Progress shows on this page.'))
                      .catch((e) => message.error(errorMessage(e)))
                  }
                >
                  {syncing ? 'Syncing…' : 'Sync from Resend'}
                </Button>
              </Tooltip>
            )}
            {['SCHEDULED', 'SENDING', 'VERIFYING'].includes(c.status) && (
              <Popconfirm title="Stop this campaign? Already-sent emails are not recalled." okText="Stop campaign" cancelText="Keep running" okButtonProps={{ danger: true, loading: cancel.isPending }} onConfirm={() => cancel.mutateAsync(c.id).then(() => message.success('Campaign stopped')).catch((e) => message.error(errorMessage(e)))}>
                <Button danger icon={<StopOutlined />}>Stop campaign</Button>
              </Popconfirm>
            )}
          </Space>
        }
      />
      <ResendSyncStatus state={c.resendSync} active={syncing} />
      {c.status === 'SENDING' && c.heldAt && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          title={`Waiting: ${c.sentCount.toLocaleString()} of ${c.recipientCount.toLocaleString()} sent so far`}
          description={<span>{heldText(c)} Nobody is emailed twice. Limits and pauses: <Link to="/tools/warmup">Tools → Email warm-up</Link>.</span>}
        />
      )}
      {!syncing && campaignReportsMissing(c) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          title="Delivery reports from Resend have not arrived for most of this campaign."
          description='The delivered, opened and clicked numbers below are too low. "Sync from Resend" fetches the missing ones.'
        />
      )}
      {stopped && (left.data?.total ?? 0) > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 24 }}
          title={`${(left.data?.total ?? 0).toLocaleString()} people in this campaign have not been emailed yet.`}
          description="Resuming emails only them, after checking them again: anyone who unsubscribed or bounced since is skipped. People who may already have received it are never emailed twice."
          action={
            <Space orientation="vertical" size={8}>
              <Popconfirm
                title={`Resume sending to ${(left.data?.total ?? 0).toLocaleString()} people?`}
                okText="Resume sending"
                okButtonProps={{ loading: resume.isPending }}
                onConfirm={() => resume.mutateAsync(c.id).then(() => message.success('Sending resumed')).catch((e) => message.error(errorMessage(e)))}
              >
                <Button type="primary" icon={<CaretRightOutlined />}>Resume sending</Button>
              </Popconfirm>
              {c.status === 'FAILED' && (
                <Popconfirm
                  title="Leave this campaign stopped? Nobody else will be emailed."
                  okText="Leave it stopped"
                  okButtonProps={{ loading: cancel.isPending }}
                  onConfirm={() => cancel.mutateAsync(c.id).then(() => message.success('Campaign left stopped')).catch((e) => message.error(errorMessage(e)))}
                >
                  <Button block icon={<StopOutlined />}>Leave it stopped</Button>
                </Popconfirm>
              )}
            </Space>
          }
        />
      )}
      <Row gutter={[24, 24]}>
        {stat(<TeamOutlined />, 'Recipients', c.recipientCount)}
        {stat(<SendOutlined />, 'Sent', c.sentCount, c.recipientCount, 'of recipients')}
        {stat(<CheckCircleOutlined />, 'Delivered', c.deliveredCount, c.sentCount)}
        {stat(<EyeOutlined />, 'Opened', c.openedCount, c.sentCount)}
        {stat(<AimOutlined />, 'Clicked', c.clickedCount, c.sentCount)}
        {stat(<WarningOutlined />, 'Bounced', c.bouncedCount, c.sentCount)}
        {stat(<UserDeleteOutlined />, 'Unsubscribed', c.unsubscribedCount, c.sentCount)}
        {stat(<MinusCircleOutlined />, 'Skipped', c.skippedCount, c.recipientCount + c.skippedCount, 'of matched contacts')}
      </Row>
      <Card title={<CardTitle title="Recipients" subtitle="Delivery and engagement per contact" />} style={{ marginTop: 24 }}>
        <div className="table-toolbar">
          <div className="toolbar-group">
            <TableSearch value={q} onSearch={(v) => { setQ(v || undefined); setPage(1); }} placeholder="Search name or email" width={280} />
          </div>
          <div className="toolbar-group">
            <Segmented value={status} onChange={(v) => { setStatus(String(v)); setPage(1); }} options={['all', 'PENDING', 'SENT', 'DELIVERED', 'OPENED', 'CLICKED', 'BOUNCED', 'COMPLAINED', 'FAILED', 'SKIPPED'].map((s) => ({ value: s, label: s === 'all' ? 'All' : statusLabel(s) }))} />
          </div>
        </div>
        <Table<RecipientItem>
          rowKey="id"
          scroll={{ x: 900 }}
          loading={tableLoading(recipients)}
          locale={tableLocale(recipients, <EmptyState description={q ? `No recipients match “${q}”` : status === 'all' ? 'No recipients yet' : `No recipients with status “${statusLabel(status)}”`} />)}
          dataSource={recipients.data?.items ?? []}
          pagination={{ current: page, pageSize, total: recipients.data?.total ?? 0, showSizeChanger: true, onChange: (p, s) => { setPage(s === pageSize ? p : 1); setPageSize(s); } }}
          columns={[
            {
              title: 'Contact',
              key: 'contact',
              width: 300,
              render: (_, r) => (r.contact ? <NameCell size={32} name={contactName(r.contact)} sub={r.email} to={`/contacts/${r.contact.id}`} /> : <NameCell size={32} name={r.email} sub="Contact deleted" />),
            },
            { title: 'Status', key: 'status', render: (_, r) => <Tag color={REC_COLOR[r.status]}>{statusLabel(r.status)}{r.skipReason ? ` · ${r.skipReason.toLowerCase().replace(/_/g, ' ')}` : ''}</Tag> },
            { title: 'Opened', key: 'opened', render: (_, r) => fmtDateTime(r.message?.openedAt) },
            { title: 'Clicked', key: 'clicked', render: (_, r) => fmtDateTime(r.message?.clickedAt) },
            { title: 'Error', dataIndex: 'error', ellipsis: true, render: (v) => v ?? '' },
            {
              key: 'view',
              width: 90,
              align: 'right',
              render: (_, r) =>
                r.message ? (
                  <Button size="small" type="link" onClick={() => setViewing({ id: r.id, email: r.email })} aria-label={`View the email sent to ${r.email}`}>
                    View
                  </Button>
                ) : null,
            },
          ]}
        />
      </Card>
      <SentEmailModal campaignId={c.id} recipient={viewing ?? null} open={viewing !== undefined} onClose={() => setViewing(undefined)} />
    </div>
  );
}

/** Where "Sync from Resend" is, or how the last one ended. */
function ResendSyncStatus({ state: s, active }: { state: ResendSyncState | null; active: boolean }) {
  if (!s) return null;
  const counts = `${s.checked.toLocaleString()}${s.total ? ` of ${s.total.toLocaleString()}` : ''} emails checked · ${plural(s.events, 'update')} recorded${s.linked ? ` · ${plural(s.linked, 'lost Resend link')} restored` : ''}`;
  if (active) {
    const percent = s.total ? Math.min(99, Math.round((s.checked / s.total) * 100)) : 0;
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
        title={s.status === 'queued' ? 'Sync with Resend is waiting its turn…' : 'Syncing with Resend…'}
        description={
          <>
            <Progress percent={percent} status="active" size="small" style={{ marginBottom: 4 }} />
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              {s.status === 'queued'
                ? 'Syncs run one at a time, and each can take most of an hour; this one starts when the ones before it finish. You can leave and come back.'
                : `${counts}. The numbers on this page update as it goes; you can leave and come back.`}
            </Typography.Text>
          </>
        }
      />
    );
  }
  if (s.status === 'failed') {
    return <Alert type="error" showIcon style={{ marginBottom: 24 }} title="The last sync with Resend stopped with an error" description={`${s.error ?? 'Unknown error'}. ${counts} before it stopped. Press Sync from Resend to carry on: what was already recorded is kept and never counted twice.`} />;
  }
  if (s.status !== 'done') {
    return <Alert type="warning" showIcon style={{ marginBottom: 24 }} title="The last sync with Resend stopped before it finished" description={`${counts} before it stopped (the server may have restarted). Press Sync from Resend to carry on: what was already recorded is kept and never counted twice.`} />;
  }
  return (
    <Typography.Paragraph type="secondary" style={{ marginTop: -8, marginBottom: 16, fontSize: 13 }}>
      <CheckCircleOutlined style={{ color: '#12B76A', marginRight: 6 }} />
      Last synced with Resend {fmtDateTime(s.finishedAt)}: {counts}.
    </Typography.Paragraph>
  );
}
